import { Plus, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import CustomDateRangePicker from '@/shared/components/ui/CustomDateRangePicker'
import DashboardPeriodFilter from '@/shared/components/ui/DashboardPeriodFilter'
import { PlaceholderPage } from '@/shared/components/ui/PlaceholderPage'
import { PERIOD_OPTIONS } from '@/shared/constants/dashboardPeriod'

import { AddLeadDialog } from '../components/leads/AddLeadDialog'
import { LeadsTable } from '../components/leads/LeadsTable'
import { SourceFilterDropdown } from '../components/leads/SourceFilterDropdown'
import { LEAD_SOURCES, PAGE_SIZE } from '../constants/leads'
import { useLeads } from '../hooks/useLeads'

export default function MarketingLeadsRoute() {
  const [searchParams, setSearchParams] = useSearchParams()
  const sourceParam = searchParams.get('source') || LEAD_SOURCES.ALL
  const statusParam = searchParams.get('status') || ''
  const warmthParam = searchParams.get('leadWarmth') || ''
  const periodParam = searchParams.get('period') || 'all'
  const startDateParam = searchParams.get('startDate') || ''
  const endDateParam = searchParams.get('endDate') || ''

  const [open, setOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const { leads, meta, filters, fetchLeads, deleteLead, isLoading } = useLeads()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [leadToDelete, setLeadToDelete] = useState(null)

  const [period, setPeriod] = useState(periodParam)
  const [customRange, setCustomRange] = useState({
    startDate: startDateParam,
    endDate: endDateParam,
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPeriod(periodParam)
    setCustomRange({
      startDate: startDateParam,
      endDate: endDateParam,
    })
  }, [periodParam, startDateParam, endDateParam])

  useEffect(() => {
    fetchLeads({
      page: 1,
      limit: PAGE_SIZE,
      search: '',
      source: sourceParam,
      status: statusParam || undefined,
      leadWarmth: warmthParam || undefined,
      period: periodParam === 'all' ? undefined : periodParam,
      startDate:
        periodParam === 'custom' ? startDateParam || undefined : undefined,
      endDate: periodParam === 'custom' ? endDateParam || undefined : undefined,
    })
  }, [
    fetchLeads,
    sourceParam,
    statusParam,
    warmthParam,
    periodParam,
    startDateParam,
    endDateParam,
  ])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput === filters.search) return
      fetchLeads({
        search: searchInput,
        page: 1,
        status: statusParam || undefined,
        leadWarmth: warmthParam || undefined,
        period: periodParam === 'all' ? undefined : periodParam,
        startDate:
          periodParam === 'custom' ? startDateParam || undefined : undefined,
        endDate:
          periodParam === 'custom' ? endDateParam || undefined : undefined,
      })
    }, 300)

    return () => window.clearTimeout(timer)
  }, [
    searchInput,
    filters.search,
    fetchLeads,
    statusParam,
    warmthParam,
    periodParam,
    startDateParam,
    endDateParam,
  ])

  const handlePeriodChange = useCallback(
    (val) => {
      setPeriod(val)
      const updated = new URLSearchParams(searchParams)
      if (val === 'all') {
        updated.delete('period')
        updated.delete('startDate')
        updated.delete('endDate')
      } else {
        updated.set('period', val)
        if (val !== 'custom') {
          updated.delete('startDate')
          updated.delete('endDate')
        }
      }
      setSearchParams(updated)
    },
    [searchParams, setSearchParams],
  )

  const handleCustomRangeChange = useCallback(
    (range) => {
      setCustomRange(range)
      const updated = new URLSearchParams(searchParams)
      updated.set('period', 'custom')
      if (range.startDate) updated.set('startDate', range.startDate)
      else updated.delete('startDate')
      if (range.endDate) updated.set('endDate', range.endDate)
      else updated.delete('endDate')
      setSearchParams(updated)
    },
    [searchParams, setSearchParams],
  )

  const handleSourceChange = useCallback(
    (source) => {
      const updated = new URLSearchParams(searchParams)
      if (source === LEAD_SOURCES.ALL) {
        updated.delete('source')
      } else {
        updated.set('source', source)
      }
      setSearchParams(updated)
      fetchLeads({
        source,
        page: 1,
        status: statusParam || undefined,
        leadWarmth: warmthParam || undefined,
        period: periodParam === 'all' ? undefined : periodParam,
        startDate:
          periodParam === 'custom' ? startDateParam || undefined : undefined,
        endDate:
          periodParam === 'custom' ? endDateParam || undefined : undefined,
      })
    },
    [
      fetchLeads,
      searchParams,
      setSearchParams,
      statusParam,
      warmthParam,
      periodParam,
      startDateParam,
      endDateParam,
    ],
  )

  const clearFilter = (key) => {
    const updated = new URLSearchParams(searchParams)
    updated.delete(key)
    if (key === 'period') {
      updated.delete('startDate')
      updated.delete('endDate')
      setPeriod('all')
      setCustomRange({ startDate: '', endDate: '' })
    }
    setSearchParams(updated)
  }

  const clearAllFilters = () => {
    const updated = new URLSearchParams(searchParams)
    updated.delete('status')
    updated.delete('leadWarmth')
    updated.delete('source')
    updated.delete('period')
    updated.delete('startDate')
    updated.delete('endDate')
    setSearchParams(updated)
    setPeriod('all')
    setCustomRange({ startDate: '', endDate: '' })
  }

  const hasActiveFilters = Boolean(
    statusParam ||
    warmthParam ||
    (sourceParam && sourceParam !== LEAD_SOURCES.ALL) ||
    (periodParam && periodParam !== 'all'),
  )

  const selectedPeriodOption = PERIOD_OPTIONS.find(
    (opt) => opt.value === periodParam,
  )

  const handlePageChange = useCallback(
    (page) => {
      fetchLeads({
        page,
        status: statusParam || undefined,
        leadWarmth: warmthParam || undefined,
        period: periodParam === 'all' ? undefined : periodParam,
        startDate:
          periodParam === 'custom' ? startDateParam || undefined : undefined,
        endDate:
          periodParam === 'custom' ? endDateParam || undefined : undefined,
      })
    },
    [
      fetchLeads,
      statusParam,
      warmthParam,
      periodParam,
      startDateParam,
      endDateParam,
    ],
  )

  const handleAdd = () => {
    setSelectedLead(null)
    setOpen(true)
  }

  const handleEdit = (lead) => {
    setSelectedLead(lead)
    setOpen(true)
  }

  const handleDialogChange = (nextOpen) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSelectedLead(null)
    }
  }

  const handleDelete = (lead) => {
    setLeadToDelete(lead)
    setConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (!leadToDelete) return

    try {
      await deleteLead(leadToDelete.id)

      toast.success('Lead deleted successfully.')

      setConfirmOpen(false)
      setLeadToDelete(null)
    } catch (error) {
      toast.error('Failed to delete lead.')
      console.error(error)
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <PlaceholderPage
        title="Leads"
        description="Track and convert your pipeline."
        action={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
            <Button
              size="lg"
              className="h-11 w-full rounded-full bg-[#0F172A] px-5 text-white hover:bg-[#0F172A] sm:w-auto"
              onClick={handleAdd}
            >
              <Plus className="size-4" />
              Add Lead
            </Button>

            <SourceFilterDropdown
              value={filters.source}
              onChange={handleSourceChange}
            />

            {period === 'custom' && (
              <CustomDateRangePicker
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={handleCustomRangeChange}
              />
            )}

            <DashboardPeriodFilter
              value={period}
              onChange={handlePeriodChange}
            />
          </div>
        }
      />

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-xs">
          <span className="text-xs font-semibold text-slate-500">
            Filtered by:
          </span>
          {periodParam && periodParam !== 'all' && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              Period:{' '}
              {selectedPeriodOption?.label ||
                periodParam.toUpperCase().replace(/_/g, ' ')}
              {periodParam === 'custom' && startDateParam && endDateParam && (
                <span className="text-[11px] font-normal text-emerald-600">
                  ({startDateParam} to {endDateParam})
                </span>
              )}
              <button
                type="button"
                onClick={() => clearFilter('period')}
                className="hover:text-emerald-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          {statusParam && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              Status: {statusParam.toUpperCase().replace(/_/g, ' ')}
              <button
                type="button"
                onClick={() => clearFilter('status')}
                className="hover:text-blue-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          {warmthParam && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
              Warmth: {warmthParam.toUpperCase()}
              <button
                type="button"
                onClick={() => clearFilter('leadWarmth')}
                className="hover:text-amber-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          {sourceParam && sourceParam !== LEAD_SOURCES.ALL && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
              Source: {sourceParam.toUpperCase()}
              <button
                type="button"
                onClick={() => clearFilter('source')}
                className="hover:text-purple-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-xs font-medium text-slate-500 underline hover:text-slate-800"
          >
            Clear all filters
          </button>
        </div>
      )}

      <LeadsTable
        data={leads}
        meta={meta}
        search={searchInput}
        onSearchChange={setSearchInput}
        onPageChange={handlePageChange}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={isLoading}
        emptyMessage="No leads match your search."
      />

      <AddLeadDialog
        open={open}
        onOpenChange={handleDialogChange}
        lead={selectedLead}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete Lead"
        description={`Are you sure you want to delete "${leadToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
      />
    </div>
  )
}
